// Shared contract for the "one flexible column" list grids.
//
// The tasks / meetings / notes grids each had their own copy of the same rule:
// a fixed table layout with a 240px floor under the single flexible column
// (title / topic / note body), so the table fills a 1440 viewport without a
// page-level horizontal scrollbar and only scrolls inside its own container on
// narrow screens — instead of squashing that column to one letter per line.
//
// The helper is deliberately a pure function, not a hook: the computation is a
// fold over the ordered columns and has no React state, so it stays trivially
// unit-testable and cannot drift between the three call sites.

/**
 * Floor under the single flexible column. Every column without a numeric
 * `width` (the title / topic / body column) is counted as this much.
 */
export const FLEX_TITLE_MIN_WIDTH = 240;

/** Width of the row-selection column when the grid has one. */
export const SELECTION_COLUMN_WIDTH = 48;

/**
 * Class for the flexible cell: clamps its text to two lines so a long value
 * wraps instead of stretching the column (rule lives in `list.css`).
 */
export const FLEX_TITLE_CELL_CLASS = 'crm-flex-title-cell';

/** Minimal shape the fold needs — a column may or may not carry a width. */
export interface WidthBearingColumn {
  readonly width?: number | string;
}

export interface TableMinWidthOptions {
  /** True when the grid renders a row-selection column. */
  readonly selection?: boolean;
}

/**
 * Minimum table width for a fixed-layout grid: the sum of every numeric column
 * width plus {@link FLEX_TITLE_MIN_WIDTH} for each flexible column, plus
 * {@link SELECTION_COLUMN_WIDTH} when the grid has a selection column.
 *
 * `scroll.x` gets this value, so below it the table scrolls inside its
 * container; at or above it the table fills the container exactly.
 */
export function tableMinWidth(
  columns: readonly WidthBearingColumn[],
  options: TableMinWidthOptions = {},
): number {
  const columnsWidth = columns.reduce(
    (sum, column) => sum + (typeof column.width === 'number' ? column.width : FLEX_TITLE_MIN_WIDTH),
    0,
  );
  return options.selection === true ? columnsWidth + SELECTION_COLUMN_WIDTH : columnsWidth;
}
